import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";

import { writeOrderDraftHandoff } from "./orderDraftHandoff";

import { useFetchLocationsQuery } from "../api/cmsPanelsApi";
import {
  useCreateSaleMutation,
  useFetchSaleItemsAvailabilityMutation,
  useScanSaleItemMutation,
  useSearchSaleItemsMutation,
} from "../api/salesApi";
import { getApiErrorMessage } from "../api/errorUtils";
import { printSaleReceipt } from "../components/Print/commerceDocumentPrint";
import SaleItemResultRow from "../components/Sales/SaleItemResultRow";
import {
  buildItemOptionChips,
  pickItemMediaFields,
  resolveItemImage,
} from "./saleItemPresentation";
import { useTranslation } from "../hooks/useTranslation";
import {
  buildSaleItemDisplayLabel,
  canIncrementLine,
  classifyOptionSegment,
  findStaleAvailabilityVariantIds,
  formatMoney,
  formatSaleDateTime,
  hasResolvedLineAvailability,
  isLineAtStockLimit,
  resolveLineStockLimit,
} from "./salesUtils";

const MANUAL_SEARCH_PAGE_SIZE = 20;

// The API returns a stable `reason` code next to an English `message`; the code
// is what we show the user, the message stays as fallback for unknown codes.
const BLOCK_REASON_KEYS = {
  duplicate_unit: "storeSales.blockedDuplicateUnit",
  not_sellable: "storeSales.blockedNotSellable",
  wrong_location: "storeSales.blockedWrongLocation",
  unit_unavailable: "storeSales.blockedUnitUnavailable",
  missing_price: "storeSales.blockedMissingPrice",
  scan_unit_label: "storeSales.blockedScanUnitLabel",
  insufficient_stock: "storeSales.outOfStockAtLocation",
};

function roundMoney(value) {
  return Number(Number(value || 0).toFixed(2));
}

function buildBasketContext(lines) {
  return {
    aggregateLines: lines
      .filter((line) => line.inventoryMode === "aggregate")
      .map((line) => ({
        variantId: line.variantId,
        quantity: line.quantity,
      })),
    serializedUnitBarcodes: lines.flatMap((line) =>
      (line.serializedUnits || []).map((unit) => unit.barcode)
    ),
  };
}

function summarizeItems(lines) {
  return lines.reduce((total, line) => total + Number(line.quantity || 0), 0);
}

function recalculateBasketLine(line, overrides = {}) {
  const baseUnitPriceAmount = Number(
    overrides.baseUnitPriceAmount ?? line.baseUnitPriceAmount ?? 0
  );
  const finalUnitPriceAmount = Number(
    overrides.finalUnitPriceAmount ?? line.finalUnitPriceAmount ?? baseUnitPriceAmount
  );
  const serializedUnits = overrides.serializedUnits ?? line.serializedUnits ?? [];
  const quantity =
    line.inventoryMode === "serialized"
      ? serializedUnits.length
      : Number(overrides.quantity ?? line.quantity ?? 0);
  const discountTotalAmount = roundMoney(
    Math.max(0, baseUnitPriceAmount - finalUnitPriceAmount) * quantity
  );
  const lineSubtotalAmount = roundMoney(finalUnitPriceAmount * quantity);

  return {
    ...line,
    ...overrides,
    baseUnitPriceAmount,
    finalUnitPriceAmount,
    quantity,
    serializedUnits,
    discountTotalAmount,
    lineSubtotalAmount,
  };
}

/**
 * Availability is only meaningful together with the location it was read at, so
 * both travel on the line. Payloads without a count (serialized units) store
 * nothing and keep following their own rules.
 */
function buildAvailabilitySnapshot(item, locationId) {
  if (
    item?.inventoryMode !== "aggregate" ||
    item?.availableQuantity === null ||
    item?.availableQuantity === undefined
  ) {
    return {};
  }

  return {
    availableQuantity: Number(item.availableQuantity),
    availabilityLocationId: locationId ?? null,
  };
}

function buildBasketLine(item, locationId) {
  const nextUnit = item.inventoryUnit
    ? { id: item.inventoryUnit.id, barcode: item.inventoryUnit.barcode }
    : null;

  return recalculateBasketLine({
    variantId: item.variantId,
    productId: item.productId,
    productTitle: item.productTitle,
    variantTitle: item.variantTitle,
    optionSummary: item.optionSummary,
    brandName: item.brandName,
    sku: item.sku,
    variantBarcode: item.variantBarcode,
    // Keep the image the server already chose (colour cover, variant, product
    // cover) so the cart row shows the same thumbnail as search or scan did.
    ...pickItemMediaFields(item),
    inventoryMode: item.inventoryMode,
    baseUnitPriceAmount: Number(item.pricing?.unitPriceAmount || 0),
    finalUnitPriceAmount: Number(item.pricing?.unitPriceAmount || 0),
    quantity: item.inventoryMode === "aggregate" ? 1 : nextUnit ? 1 : 0,
    serializedUnits: nextUnit ? [nextUnit] : [],
    ...buildAvailabilitySnapshot(item, locationId),
  });
}

const SalesPage = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const paymentOptions = useMemo(
    () => [
      { value: "CASH", label: t("commerceCommon.paymentCash"), icon: "icon-money-coins" },
      { value: "CARD", label: t("commerceCommon.paymentCard"), icon: "icon-credit-card" },
      { value: "BANK", label: t("commerceCommon.paymentBankTransfer"), icon: "icon-bank" },
    ],
    [t]
  );
  const scanInputRef = useRef(null);
  const manualSearchInputRef = useRef(null);
  const activeManualSearchContextRef = useRef(null);
  const basketRef = useRef([]);
  const availabilityRequestRef = useRef(null);
  const [locationId, setLocationId] = useState("");
  const [scanValue, setScanValue] = useState("");
  const [manualSearchValue, setManualSearchValue] = useState("");
  const [manualSearchResults, setManualSearchResults] = useState([]);
  const [manualSearchPagination, setManualSearchPagination] = useState(null);
  const [isLoadingMoreSearch, setIsLoadingMoreSearch] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [bankName, setBankName] = useState("");
  const [basket, setBasket] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [completedSale, setCompletedSale] = useState(null);
  const [editingPriceVariantId, setEditingPriceVariantId] = useState(null);
  const [priceEditorValue, setPriceEditorValue] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const { data: locations = [], isFetching: isFetchingLocations } =
    useFetchLocationsQuery();
  const [searchSaleItems, { isLoading: isSearching }] = useSearchSaleItemsMutation();
  const [scanSaleItem, { isLoading: isScanning }] = useScanSaleItemMutation();
  const [createSale, { isLoading: isCreatingSale }] = useCreateSaleMutation();
  const [fetchSaleItemsAvailability, { isLoading: isRevalidatingStock }] =
    useFetchSaleItemsAvailabilityMutation();

  useEffect(() => {
    if (!locationId && locations.length) {
      setLocationId(String(locations[0].id));
    }
  }, [locationId, locations]);

  const clearManualSearchState = () => {
    setManualSearchResults([]);
    setManualSearchPagination(null);
    activeManualSearchContextRef.current = null;
  };

  const matchesActiveManualSearchContext = (query, nextLocationId = locationId) => {
    const activeContext = activeManualSearchContextRef.current;
    if (!activeContext) {
      return false;
    }

    return (
      activeContext.query === query &&
      String(activeContext.locationId) === String(nextLocationId)
    );
  };

  useEffect(() => {
    clearManualSearchState();
  }, [locationId]);

  useEffect(() => {
    basketRef.current = basket;
  }, [basket]);

  // Lines only ever go stale when the selected location changes: everything
  // added by scan or search already carries availability for the current one.
  useEffect(() => {
    revalidateAvailability(locationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    if (!completedSale) {
      const frame = window.requestAnimationFrame(() => {
        const activeInput = showSearch
          ? manualSearchInputRef.current
          : scanInputRef.current;
        activeInput?.focus();
        activeInput?.select?.();
      });
      return () => window.cancelAnimationFrame(frame);
    }
    return undefined;
  }, [completedSale, locationId, basket.length, showSearch]);

  const selectedLocation = useMemo(
    () => locations.find((entry) => String(entry.id) === String(locationId)) || null,
    [locationId, locations]
  );

  const subtotalAmount = useMemo(
    () =>
      roundMoney(
        basket.reduce(
          (total, line) =>
            total + Number(line.baseUnitPriceAmount || 0) * Number(line.quantity || 0),
          0
        )
      ),
    [basket]
  );

  const discountTotalAmount = useMemo(
    () =>
      roundMoney(
        basket.reduce((total, line) => total + Number(line.discountTotalAmount || 0), 0)
      ),
    [basket]
  );

  const totalAmount = useMemo(
    () =>
      roundMoney(
        basket.reduce((total, line) => total + Number(line.lineSubtotalAmount || 0), 0)
      ),
    [basket]
  );

  const setWorkerFeedback = (type, message, extra = {}) => {
    setFeedback({
      type,
      message,
      hint: extra.hint || null,
      action: extra.action || null,
      id: `${Date.now()}-${Math.random()}`,
    });
  };

  const selectScanMode = () => {
    setShowSearch(false);
    setFeedback(null);
  };

  const selectSearchMode = () => {
    setShowSearch(true);
    setFeedback(null);
  };

  const revalidateAvailability = async (nextLocationId) => {
    const staleVariantIds = findStaleAvailabilityVariantIds(
      basketRef.current,
      nextLocationId
    );
    if (!nextLocationId || !staleVariantIds.length) {
      return;
    }

    const requested = new Set(staleVariantIds);
    availabilityRequestRef.current = nextLocationId;

    try {
      const result = await fetchSaleItemsAvailability({
        locationId: Number(nextLocationId),
        variantIds: staleVariantIds,
      }).unwrap();

      // A quick second location switch supersedes this response; applying it
      // would stamp lines with stock read for a location nobody is selling at.
      if (availabilityRequestRef.current !== nextLocationId) {
        return;
      }

      const availabilityByVariant = new Map(
        (result?.items || []).map((item) => [item.variantId, item])
      );
      // A variant the catalog no longer returns is not sellable at this
      // location, so it resolves to 0 rather than staying unknown forever.
      const availableFor = (variantId) =>
        Number(availabilityByVariant.get(variantId)?.availableQuantity || 0);

      setBasket((current) =>
        current.map((line) =>
          line.inventoryMode === "aggregate" && requested.has(line.variantId)
            ? recalculateBasketLine(line, {
                availableQuantity: availableFor(line.variantId),
                availabilityLocationId: nextLocationId,
              })
            : line
        )
      );

      const hasLineOverStock = basketRef.current.some(
        (line) =>
          line.inventoryMode === "aggregate" &&
          requested.has(line.variantId) &&
          Number(line.quantity || 0) > availableFor(line.variantId)
      );

      if (hasLineOverStock) {
        setWorkerFeedback("warning", t("storeSales.stockBelowBasket"));
      }
    } catch (error) {
      if (availabilityRequestRef.current !== nextLocationId) {
        return;
      }
      setWorkerFeedback(
        "warning",
        getApiErrorMessage(error, t("storeSales.stockRevalidationError")),
        { action: "retryStock" }
      );
    }
  };

  const clearManualSearchInput = () => {
    setManualSearchValue("");
    clearManualSearchState();
    manualSearchInputRef.current?.focus();
  };

  const resetScanField = () => {
    setScanValue("");
    window.requestAnimationFrame(() => {
      scanInputRef.current?.focus();
      scanInputRef.current?.select?.();
    });
  };

  const addBasketItem = (item) => {
    setBasket((current) => {
      if (item.inventoryMode === "aggregate") {
        const existing = current.find((line) => line.variantId === item.variantId);
        if (!existing) return [...current, buildBasketLine(item, locationId)];
        return current.map((line) =>
          line.variantId === item.variantId
            ? recalculateBasketLine(line, {
                quantity: line.quantity + 1,
                ...buildAvailabilitySnapshot(item, locationId),
              })
            : line
        );
      }

      const existing = current.find((line) => line.variantId === item.variantId);
      const nextUnit = item.inventoryUnit
        ? { id: item.inventoryUnit.id, barcode: item.inventoryUnit.barcode }
        : null;

      if (!existing) return [...current, buildBasketLine(item, locationId)];
      return current.map((line) =>
        line.variantId === item.variantId
          ? recalculateBasketLine(line, {
              serializedUnits: nextUnit
                ? [...(line.serializedUnits || []), nextUnit]
                : line.serializedUnits,
            })
          : line
      );
    });
  };

  const handleScanSubmit = async (event) => {
    event.preventDefault();
    const barcode = String(scanValue || "").trim();
    if (!locationId) {
      setWorkerFeedback("warning", t("storeSales.chooseLocationBeforeScan"));
      resetScanField();
      return;
    }
    if (!barcode) {
      resetScanField();
      return;
    }
    try {
      const result = await scanSaleItem({
        locationId: Number(locationId),
        barcode,
        basketContext: buildBasketContext(basket),
      }).unwrap();

      if (result?.status === "ready") {
        addBasketItem(result.item);
        setWorkerFeedback(
          "success",
          t("commerceCommon.itemAddedToBasket").replace(
            "{name}",
            result.item?.variantTitle || result.item?.productTitle || t("commerceCommon.itemFallback")
          )
        );
      } else {
        const isNotFound = result?.status === "not_found";
        const blockReasonKey = BLOCK_REASON_KEYS[result?.reason];
        let localizedMessage;
        if (isNotFound) {
          localizedMessage = t("storeSales.scanNotFound");
        } else if (result?.status === "ambiguous") {
          localizedMessage = t("storeSales.scanAmbiguous");
        } else if (blockReasonKey) {
          localizedMessage = t(blockReasonKey);
        } else {
          localizedMessage = result?.message || t("storeSales.barcodeNotSold");
        }

        setWorkerFeedback(
          isNotFound ? "danger" : "warning",
          localizedMessage,
          result?.reason === "duplicate_unit"
            ? {}
            : { hint: t("storeSales.scanNotFoundHint"), action: "manualSearch" }
        );
      }
    } catch (error) {
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeSales.scanProcessError"))
      );
    } finally {
      resetScanField();
    }
  };

  const handleManualSearchSubmit = async (event) => {
    event.preventDefault();
    const query = String(manualSearchValue || "").trim();
    if (!locationId) {
      setWorkerFeedback("warning", t("storeSales.chooseLocationBeforeSearch"));
      manualSearchInputRef.current?.focus();
      return;
    }
    if (!query) {
      manualSearchInputRef.current?.focus();
      return;
    }

    const searchContext = {
      query,
      locationId: Number(locationId),
    };
    activeManualSearchContextRef.current = searchContext;
    clearManualSearchState();
    activeManualSearchContextRef.current = searchContext;

    try {
      const result = await searchSaleItems({
        locationId: searchContext.locationId,
        query,
        limit: MANUAL_SEARCH_PAGE_SIZE,
        offset: 0,
      }).unwrap();

      if (!matchesActiveManualSearchContext(query, searchContext.locationId)) {
        return;
      }

      setManualSearchResults(result?.items || []);
      setManualSearchPagination(result?.pagination || null);

      if (!(result?.items || []).length) {
        setWorkerFeedback("warning", t("storeSales.noSellableItem"));
      }
    } catch (error) {
      if (!matchesActiveManualSearchContext(query, searchContext.locationId)) {
        return;
      }
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeSales.searchError"))
      );
    }
  };

  const handleLoadMoreManualSearch = async () => {
    const activeContext = activeManualSearchContextRef.current;
    const query = activeContext?.query;
    const searchLocationId = activeContext?.locationId;
    if (
      !query ||
      !searchLocationId ||
      !locationId ||
      String(searchLocationId) !== String(locationId) ||
      !manualSearchPagination?.hasMore ||
      isLoadingMoreSearch ||
      isSearching
    ) {
      return;
    }

    setIsLoadingMoreSearch(true);
    const requestOffset = Number(
      manualSearchPagination?.nextOffset ?? manualSearchResults.length
    );

    try {
      const result = await searchSaleItems({
        locationId: searchLocationId,
        query,
        limit: MANUAL_SEARCH_PAGE_SIZE,
        offset: requestOffset,
      }).unwrap();

      if (!matchesActiveManualSearchContext(query, searchLocationId)) {
        return;
      }

      setManualSearchResults((current) => {
        const seen = new Set(current.map((item) => item.variantId));
        const appended = (result?.items || []).filter((item) => !seen.has(item.variantId));
        return [...current, ...appended];
      });
      setManualSearchPagination(result?.pagination || null);
    } catch (error) {
      if (!matchesActiveManualSearchContext(query, searchLocationId)) {
        return;
      }
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeSales.searchError"))
      );
    } finally {
      setIsLoadingMoreSearch(false);
    }
  };

  const handleAddManualSearchItem = (item) => {
    if (!item?.canAddManually) {
      const blockReasonKey = BLOCK_REASON_KEYS[item?.reason];
      setWorkerFeedback(
        "warning",
        blockReasonKey
          ? t(blockReasonKey)
          : item?.message || t("storeSales.cannotAddManually")
      );
      return;
    }
    const currentAggregateQuantity =
      basket.find((line) => line.variantId === item.variantId)?.quantity || 0;
    if (
      item.inventoryMode === "aggregate" &&
      currentAggregateQuantity >= Number(item.availableQuantity || 0)
    ) {
      setWorkerFeedback("warning", t("storeSales.outOfStockAtLocation"));
      return;
    }
    addBasketItem(item);
    setWorkerFeedback(
      "success",
      t("commerceCommon.itemAddedToBasket").replace(
        "{name}",
        item.variantTitle || item.productTitle || t("commerceCommon.itemFallback")
      )
    );
    setManualSearchValue("");
    clearManualSearchState();
    resetScanField();
  };

  const handleRemoveOne = (variantId) => {
    setBasket((current) =>
      current.flatMap((line) => {
        if (line.variantId !== variantId) return [line];
        if (line.inventoryMode === "aggregate") {
          if (line.quantity <= 1) return [];
          return [recalculateBasketLine(line, { quantity: line.quantity - 1 })];
        }
        const nextUnits = (line.serializedUnits || []).slice(0, -1);
        if (!nextUnits.length) return [];
        return [recalculateBasketLine(line, { serializedUnits: nextUnits })];
      })
    );
  };

  const handleAddOne = (line) => {
    if (line.inventoryMode !== "aggregate") return;

    if (!canIncrementLine(line, locationId)) {
      const stockLimit = resolveLineStockLimit(line, locationId);
      setWorkerFeedback(
        "warning",
        stockLimit === null
          ? t("storeSales.stockUnresolved")
          : t("storeSales.maxStockReached").replace("{count}", stockLimit)
      );
      return;
    }

    setBasket((current) =>
      current.map((entry) =>
        entry.variantId === line.variantId && canIncrementLine(entry, locationId)
          ? recalculateBasketLine(entry, { quantity: entry.quantity + 1 })
          : entry
      )
    );
  };

  const handleRemoveLine = (variantId) => {
    setBasket((current) => current.filter((line) => line.variantId !== variantId));
    if (editingPriceVariantId === variantId) {
      setEditingPriceVariantId(null);
      setPriceEditorValue("");
    }
  };

  const handleStartPriceEdit = (line) => {
    setEditingPriceVariantId(line.variantId);
    setPriceEditorValue(String(line.finalUnitPriceAmount ?? line.baseUnitPriceAmount ?? ""));
  };

  const handleApplyPriceEdit = (line) => {
    const normalized = String(priceEditorValue || "").trim().replace(",", ".");
    const nextPrice = Number(normalized);
    if (!normalized || Number.isNaN(nextPrice)) {
      setWorkerFeedback("warning", t("storeSales.invalidUnitPrice"));
      return;
    }
    if (nextPrice < 0) {
      setWorkerFeedback("warning", t("storeSales.negativeUnitPrice"));
      return;
    }
    if (nextPrice > Number(line.baseUnitPriceAmount || 0)) {
      setWorkerFeedback(
        "warning",
        t("storeSales.priceExceedsOriginal")
      );
      return;
    }
    setBasket((current) =>
      current.map((entry) =>
        entry.variantId === line.variantId
          ? recalculateBasketLine(entry, { finalUnitPriceAmount: roundMoney(nextPrice) })
          : entry
      )
    );
    setEditingPriceVariantId(null);
    setPriceEditorValue("");
  };

  /**
   * Hands the current basket to the manual order wizard. Deliberately does not
   * touch the sale flow, the basket state or inventory: only variant ids and
   * quantities travel, and the wizard re-fetches everything else.
   */
  const handleCreateOrderFromBasket = () => {
    const handed = writeOrderDraftHandoff(
      basket.map((line) => ({
        catalogVariantId: line.variantId,
        quantity: line.quantity,
      })),
    );

    navigate(handed ? "/admin/orders/create?from=sales" : "/admin/orders/create");
  };

  const handleCompleteSale = async () => {
    if (!locationId) {
      setWorkerFeedback("warning", t("storeSales.chooseLocationBeforeComplete"));
      return;
    }
    if (!basket.length) {
      setWorkerFeedback("warning", t("storeSales.addItemBeforeComplete"));
      return;
    }
    try {
      const result = await createSale({
        locationId: Number(locationId),
        paymentMethod,
        bankName:
          paymentMethod === "CASH" ? undefined : String(bankName || "").trim() || undefined,
        lines: basket.map((line) => ({
          catalogVariantId: line.variantId,
          quantity: line.inventoryMode === "aggregate" ? line.quantity : undefined,
          serializedUnitBarcodes:
            line.inventoryMode === "serialized"
              ? line.serializedUnits.map((unit) => unit.barcode)
              : undefined,
          finalUnitPriceAmount:
            roundMoney(line.finalUnitPriceAmount) !== roundMoney(line.baseUnitPriceAmount)
              ? roundMoney(line.finalUnitPriceAmount)
              : undefined,
        })),
      }).unwrap();

      setCompletedSale(result);
      setBasket([]);
      setBankName("");
      setFeedback(null);
      setEditingPriceVariantId(null);
      setPriceEditorValue("");
      setManualSearchResults([]);
      setManualSearchValue("");
    } catch (error) {
      setWorkerFeedback(
        "danger",
        getApiErrorMessage(error, t("storeSales.completeSaleError"), {
          409: t("storeSales.itemsUnavailable"),
        })
      );
    }
  };

  const startNewSale = () => {
    setCompletedSale(null);
    setBasket([]);
    setPaymentMethod("CASH");
    setBankName("");
    setFeedback(null);
    setManualSearchResults([]);
    setManualSearchValue("");
    setEditingPriceVariantId(null);
    setPriceEditorValue("");
    setShowSearch(false);
    resetScanField();
  };

  const itemCount = summarizeItems(basket);

  /* ────────────────────────────────────────────── */

  const renderFeedback = () => {
    if (!feedback) {
      return null;
    }

    const isSuccess = feedback.type === "success";

    return (
      <div
        className={`pos-feedback pos-feedback--${feedback.type}`}
        key={feedback.id}
        role={isSuccess ? "status" : "alert"}
      >
        <i
          className={`tim-icons ${
            isSuccess ? "icon-check-2" : "icon-alert-circle-exc"
          } pos-feedback__icon`}
          aria-hidden="true"
        />
        <div className="pos-feedback__body">
          <p className="pos-feedback__title">{feedback.message}</p>
          {feedback.hint && <p className="pos-feedback__hint">{feedback.hint}</p>}
          {feedback.action === "manualSearch" && !showSearch && (
            <Button
              color="primary"
              size="sm"
              className="pos-feedback__action"
              onClick={selectSearchMode}
            >
              <i className="tim-icons icon-zoom-split" aria-hidden="true" />
              {t("storeSales.searchModeTab")}
            </Button>
          )}
          {feedback.action === "retryStock" && (
            <Button
              color="primary"
              size="sm"
              className="pos-feedback__action"
              disabled={isRevalidatingStock}
              onClick={() => revalidateAvailability(locationId)}
            >
              <i className="tim-icons icon-refresh-01" aria-hidden="true" />
              {isRevalidatingStock
                ? t("storeSales.checkingStock")
                : t("storeSales.retryStockCheck")}
            </Button>
          )}
        </div>
        <button
          type="button"
          className="pos-feedback__close"
          onClick={() => setFeedback(null)}
          aria-label={t("commerceCommon.close")}
        >
          <i className="tim-icons icon-simple-remove" aria-hidden="true" />
        </button>
      </div>
    );
  };

  // Option dimension names come from the catalog in mixed languages; label the
  // known ones in the user's language and pass anything else through untouched.
  const buildOptionChips = (optionSummary) =>
    String(optionSummary || "")
      .split(" / ")
      .map((segment) => classifyOptionSegment(segment))
      .filter(Boolean)
      .map((option) => {
        if (option.kind === "color") {
          return t("commerceCommon.optionColorLabel").replace("{value}", option.value);
        }
        if (option.kind === "size") {
          return t("commerceCommon.optionSizeLabel").replace("{value}", option.value);
        }
        return option.name ? `${option.name}: ${option.value}` : option.value;
      });

  const renderManualSearchItem = (item) => {
    const title =
      item.productTitle ||
      buildSaleItemDisplayLabel({
        productTitle: item.productTitle,
        variantTitle: item.variantTitle,
        optionSummary: item.optionSummary,
      });
    const isAggregate = item.inventoryMode === "aggregate";
    const availableQuantity = Number(item.availableQuantity || 0);
    const hasStock = isAggregate && availableQuantity > 0;
    const blockReasonKey = BLOCK_REASON_KEYS[item.reason];
    // Mirrors the guard inside handleAddManualSearchItem so the limit is visible
    // before the click, not only as a warning after it.
    const basketQuantity =
      basket.find((line) => line.variantId === item.variantId)?.quantity || 0;
    const atStockLimit =
      isAggregate && item.canAddManually && basketQuantity >= availableQuantity;
    const disabledReason = item.canAddManually
      ? atStockLimit
        ? t("storeSales.maxStockReached").replace("{count}", availableQuantity)
        : null
      : blockReasonKey
      ? t(blockReasonKey)
      : item.message || t("storeSales.cannotAddManually");

    let stockLabel;
    let stockTone;
    if (!isAggregate) {
      stockLabel = t("commerceCommon.scanLabel");
      stockTone = "neutral";
    } else if (hasStock) {
      stockLabel = t("storeSales.inStockCount").replace("{count}", availableQuantity);
      stockTone = "ok";
    } else {
      stockLabel = t("storeSales.outOfStock");
      stockTone = "empty";
    }

    let actionLabel;
    if (atStockLimit) {
      actionLabel = t("storeSales.allInBasket");
    } else if (item.canAddManually) {
      actionLabel = t("storeSales.addToBasket");
    } else if (!isAggregate) {
      actionLabel = t("commerceCommon.scanLabel");
    } else {
      actionLabel = t("storeSales.outOfStockShort");
    }

    const isAddable = item.canAddManually && !atStockLimit;
    let stockHint = null;
    if (disabledReason && isAggregate && hasStock) {
      stockHint = disabledReason;
    } else if (hasStock && selectedLocation) {
      stockHint = t("storeSales.atLocation").replace(
        "{location}",
        selectedLocation.name || t("commerceCommon.selectedLocation")
      );
    }

    // Double click calls the same handler as the button, so every stock,
    // serialized and duplicate rule still applies; only addable rows react.
    return (
      <SaleItemResultRow
        key={item.variantId}
        item={item}
        title={title}
        chips={buildItemOptionChips(item.optionSummary, t)}
        sku={item.sku}
        price={formatMoney(item.pricing?.unitPriceAmount)}
        stockLabel={stockLabel}
        stockTone={stockTone}
        stockHint={stockHint}
        actionLabel={actionLabel}
        isAddable={isAddable}
        actionDisabled={isCreatingSale}
        actionTitle={disabledReason}
        actionAriaLabel={
          isAddable
            ? `${t("storeSales.addToBasket")} — ${title}`
            : `${disabledReason} — ${title}`
        }
        doubleClickHint={t("storeSales.doubleClickToAdd")}
        onAdd={handleAddManualSearchItem}
      />
    );
  };

  // The API returns the payment method as a lowercase code; reuse the labels the
  // payment picker already renders so the receipt never shows a raw code.
  const resolvePaymentLabel = (paymentMethod) => {
    const normalized = String(paymentMethod || "").toUpperCase();
    return (
      paymentOptions.find((option) => option.value === normalized)?.label ||
      paymentMethod ||
      "—"
    );
  };

  const renderSaleFact = (icon, label, value, note = null) => (
    <div className="pos-fact" key={label}>
      <span className="pos-fact__icon" aria-hidden="true">
        <i className={`tim-icons ${icon}`} />
      </span>
      <span className="pos-fact__body">
        <span className="pos-fact__label">{label}</span>
        <span className="pos-fact__value">{value}</span>
        {note && <span className="pos-fact__note">{note}</span>}
      </span>
    </div>
  );

  const renderCheckoutPanel = (sticky = true) => (
    <aside
      className={`sales-page-checkout${sticky ? " sales-page-checkout--sticky" : ""}`}
      aria-label={t("storeSales.saleSummaryTitle")}
    >
      <div className="sales-page-checkout__inner pos-checkout">
        <h2 className="pos-checkout__title">{t("storeSales.saleSummaryTitle")}</h2>

        <div className="pos-summary">
          <div className="pos-summary-row">
            <span>{t("commerceCommon.subtotal")}</span>
            <span>{formatMoney(subtotalAmount)}</span>
          </div>
          <div className="pos-summary-row">
            <span>{t("commerceCommon.discount")}</span>
            <span className={discountTotalAmount > 0 ? "sp-discount" : undefined}>
              {discountTotalAmount > 0
                ? `−${formatMoney(discountTotalAmount)}`
                : formatMoney(0)}
            </span>
          </div>
          <div className="pos-summary-total">
            <span>{t("commerceCommon.total")}</span>
            <span>{formatMoney(totalAmount)}</span>
          </div>
        </div>

        <div className="pos-payment">
          <span className="sp-label">{t("commerceCommon.payment")}</span>
          <div className="pos-payment-options" role="radiogroup" aria-label={t("commerceCommon.payment")}>
            {paymentOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={paymentMethod === opt.value}
                className={`pos-payment-option${
                  paymentMethod === opt.value ? " is-active" : ""
                }`}
                onClick={() => setPaymentMethod(opt.value)}
                disabled={isCreatingSale}
              >
                <i className={`tim-icons ${opt.icon}`} aria-hidden="true" />
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
          {paymentMethod !== "CASH" && (
            <FormGroup className="pos-bank-field">
              <Label for="sales-bank-name">{t("commerceCommon.bankProvider")}</Label>
              <Input
                id="sales-bank-name"
                type="text"
                placeholder={t("commerceCommon.optional")}
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                disabled={isCreatingSale}
              />
            </FormGroup>
          )}
        </div>

        <Button
          color="success"
          className="pos-complete-btn"
          onClick={handleCompleteSale}
          disabled={!basket.length || isCreatingSale}
        >
          {isCreatingSale ? t("commerceCommon.completing") : t("storeSales.completeSale")}
        </Button>

        {/*
          Secondary path: hand this selection to the Orders wizard instead of
          completing a sale. Creates no Sale and mutates no stock - the wizard
          re-reads authoritative price/stock and reserves only on order creation.
        */}
        <Button
          color="secondary"
          outline
          className="pos-create-order-btn"
          onClick={handleCreateOrderFromBasket}
          disabled={!basket.length || isCreatingSale}
        >
          {t("storeSales.createOrderFromBasket")}
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="content sales-premium sales-page pos-redesign">
      <Card className="sales-page-card">
        <CardHeader className="sales-page-header">
          <div>
            <div className="sp-title">{t("storeSales.title")}</div>
            <p className="sp-subtitle">{t("storeSales.subtitle")}</p>
          </div>
        </CardHeader>

        <CardBody className="sales-page-body">
          {completedSale ? (
            <div className="pos-success">
              <div className="pos-success__badge" aria-hidden="true">
                <i className="tim-icons icon-check-2" />
              </div>
              <h2 className="pos-success__title">{t("storeSales.saleCompletedTitle")}</h2>
              <p className="pos-success__subtitle">
                {t("storeSales.saleCompletedSubtitle")}
              </p>

              <div className="pos-success__facts">
                {renderSaleFact(
                  "icon-notes",
                  t("commerceCommon.saleNumber"),
                  completedSale.saleNumber
                )}
                {renderSaleFact(
                  "icon-credit-card",
                  t("commerceCommon.payment"),
                  resolvePaymentLabel(completedSale.paymentMethod),
                  completedSale.bankName
                )}
                {completedSale.location?.name &&
                  renderSaleFact(
                    "icon-square-pin",
                    t("commerceCommon.location"),
                    completedSale.location.name
                  )}
                {completedSale.completedAt &&
                  renderSaleFact(
                    "icon-calendar-60",
                    t("storeSales.dateAndTime"),
                    formatSaleDateTime(completedSale.completedAt)
                  )}
              </div>

              <div className="pos-success__total">
                <span className="pos-success__total-label">
                  {t("storeSales.totalPaid")}
                </span>
                <span className="pos-success__total-value">
                  {formatMoney(completedSale.totalAmount)}
                </span>
              </div>

              <h3 className="pos-success__section-title">{t("storeSales.soldItems")}</h3>

              <div className="pos-receipt-table">
                <div className="pos-receipt-row pos-receipt-row--header" aria-hidden="true">
                  <span>#</span>
                  <span>{t("commerceCommon.itemHeader")}</span>
                  <span>{t("commerceCommon.price")}</span>
                  <span>{t("commerceCommon.qty")}</span>
                  <span>{t("commerceCommon.total")}</span>
                </div>
                {(completedSale.lines || []).map((line, index) => {
                  const lineTitle =
                    line.productTitle ||
                    buildSaleItemDisplayLabel({
                      productTitle: line.productTitle,
                      variantTitle: line.variantTitle,
                      optionSummary: line.optionSummary,
                    });
                  const lineChips = buildOptionChips(line.optionSummary);

                  return (
                    <div className="pos-receipt-row" key={line.id}>
                      <span className="pos-receipt-row__index">{index + 1}</span>
                      <div className="pos-receipt-row__item">
                        <span className="pos-item-label">{lineTitle}</span>
                        {line.sku && (
                          <span className="pos-basket-row__meta sp-truncate">
                            {line.sku}
                          </span>
                        )}
                        {(lineChips.length > 0 || line.brandName) && (
                          <div className="pos-result-row__chips">
                            {line.brandName && (
                              <span className="pos-result-chip">{line.brandName}</span>
                            )}
                            {lineChips.map((chip) => (
                              <span className="pos-result-chip" key={chip}>
                                {chip}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="pos-receipt-row__price">
                        <span>{formatMoney(line.finalUnitPriceAmount)}</span>
                        {line.discountTotalAmount > 0 && (
                          <span className="pos-basket-row__was">
                            {t("commerceCommon.wasPrice").replace(
                              "{amount}",
                              formatMoney(line.unitPriceAmount)
                            )}
                          </span>
                        )}
                      </div>
                      <span className="pos-receipt-row__qty">{line.quantity}</span>
                      <span className="pos-receipt-row__total">
                        {formatMoney(line.lineSubtotalAmount)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="pos-receipt-totals">
                <div className="pos-summary-row">
                  <span>{t("commerceCommon.subtotal")}</span>
                  <span>{formatMoney(completedSale.subtotalAmount)}</span>
                </div>
                <div className="pos-summary-row">
                  <span>{t("commerceCommon.discount")}</span>
                  <span
                    className={
                      completedSale.discountTotalAmount > 0 ? "sp-discount" : undefined
                    }
                  >
                    {completedSale.discountTotalAmount > 0
                      ? `−${formatMoney(completedSale.discountTotalAmount)}`
                      : formatMoney(0)}
                  </span>
                </div>
                <div className="pos-summary-total">
                  <span>{t("commerceCommon.total")}</span>
                  <span>{formatMoney(completedSale.totalAmount)}</span>
                </div>
              </div>

              <div className="pos-success__actions">
                <Button
                  color="success"
                  className="pos-success-action pos-success-action--primary"
                  onClick={startNewSale}
                >
                  <i className="tim-icons icon-simple-add" aria-hidden="true" />
                  <span className="pos-success-action__text">
                    <span className="pos-success-action__title">
                      {t("commerceCommon.newSale")}
                    </span>
                    <span className="pos-success-action__hint">
                      {t("storeSales.newSaleHint")}
                    </span>
                  </span>
                </Button>
                <Button
                  color="secondary"
                  className="pos-success-action"
                  onClick={() => printSaleReceipt(completedSale)}
                >
                  <i className="tim-icons icon-paper" aria-hidden="true" />
                  <span className="pos-success-action__text">
                    <span className="pos-success-action__title">
                      {t("commerceCommon.printReceipt")}
                    </span>
                    <span className="pos-success-action__hint">
                      {t("storeSales.printReceiptHint")}
                    </span>
                  </span>
                </Button>
              </div>
            </div>
          ) : (
            <div
              className={`sales-page-layout${
                basket.length > 0 ? " sales-page-layout--split" : ""
              }`}
            >
              <div className="sales-page-main">
                <section className="pos-scan-card">
                  {renderFeedback()}

                  <div className="pos-scan-card__top">
                    <FormGroup className="pos-location-field">
                      <Label for="sales-location">{t("commerceCommon.location")}</Label>
                      <Input
                        id="sales-location"
                        type="select"
                        value={locationId}
                        onChange={(e) => setLocationId(e.target.value)}
                        disabled={isFetchingLocations || isCreatingSale}
                      >
                        <option value="">{t("commerceCommon.chooseLocation")}</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name ||
                              t("commerceCommon.locationFallback").replace("{id}", loc.id)}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>

                    <div className="pos-mode-tabs" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={!showSearch}
                        className={`pos-mode-tab${!showSearch ? " is-active" : ""}`}
                        onClick={selectScanMode}
                      >
                        <i className="tim-icons icon-tag" aria-hidden="true" />
                        {t("storeSales.scanModeTab")}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={showSearch}
                        className={`pos-mode-tab${showSearch ? " is-active" : ""}`}
                        onClick={selectSearchMode}
                      >
                        <i className="tim-icons icon-zoom-split" aria-hidden="true" />
                        {t("storeSales.searchModeTab")}
                      </button>
                    </div>
                  </div>

                  {showSearch ? (
                    <>
                      <Form className="pos-search-form" onSubmit={handleManualSearchSubmit}>
                        <Label for="sales-search-input" className="sr-only">
                          {t("storeSales.searchPlaceholder")}
                        </Label>
                        <div className="pos-field-wrap">
                          <div className="pos-field-control">
                            <i
                              className="tim-icons icon-zoom-split pos-field-icon"
                              aria-hidden="true"
                            />
                            <Input
                              id="sales-search-input"
                              innerRef={manualSearchInputRef}
                              type="text"
                              autoComplete="off"
                              className="pos-field-input"
                              placeholder={t("storeSales.searchPlaceholder")}
                              value={manualSearchValue}
                              onChange={(e) => setManualSearchValue(e.target.value)}
                              disabled={!locationId || isSearching || isCreatingSale}
                              aria-label={t("storeSales.searchPlaceholder")}
                            />
                            {manualSearchValue && (
                              <button
                                type="button"
                                className="pos-field-clear"
                                onClick={clearManualSearchInput}
                                aria-label={t("commerceCommon.clearLookup")}
                              >
                                <i
                                  className="tim-icons icon-simple-remove"
                                  aria-hidden="true"
                                />
                              </button>
                            )}
                          </div>
                          <Button
                            color="primary"
                            type="submit"
                            className="pos-field-submit"
                            disabled={
                              !locationId ||
                              !manualSearchValue.trim() ||
                              isSearching ||
                              isCreatingSale
                            }
                          >
                            {isSearching
                              ? t("commerceCommon.searching")
                              : t("commerceCommon.search")}
                          </Button>
                        </div>
                        <p className="pos-scan-hint">{t("storeSales.searchHint")}</p>
                      </Form>

                      {(manualSearchResults.length > 0 || manualSearchPagination?.hasMore) && (
                        <div className="pos-search-results">
                          {manualSearchPagination?.total > 0 && (
                            <p className="pos-search-results__count">
                              {t("storeSales.searchFoundCount")
                                .replace("{count}", manualSearchPagination.total)
                                .replace(
                                  "{query}",
                                  activeManualSearchContextRef.current?.query || ""
                                )}
                            </p>
                          )}
                          <div className="pos-search-results__list sir-list sp-scrollbar-hidden">
                            {manualSearchResults.map(renderManualSearchItem)}
                          </div>
                          {manualSearchPagination?.hasMore && (
                            <div className="pos-search-results__more">
                              <p className="pos-search-results__shown">
                                {t("storeSales.searchResultsSummary")
                                  .replace("{shown}", manualSearchResults.length)
                                  .replace("{total}", manualSearchPagination.total)}
                              </p>
                              <Button
                                color="secondary"
                                disabled={isLoadingMoreSearch || isSearching || isCreatingSale}
                                onClick={handleLoadMoreManualSearch}
                              >
                                {isLoadingMoreSearch
                                  ? t("commerceCommon.loading")
                                  : t("storeSales.loadMoreSearchResults")}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <Form className="pos-scan-form" onSubmit={handleScanSubmit}>
                      <Label for="sales-scan-input" className="sr-only">
                        {t("storeSales.scanBarcode")}
                      </Label>
                      <div className="pos-field-wrap">
                        <div className="pos-field-control">
                          <i
                            className="tim-icons icon-tag pos-field-icon pos-field-icon--scan"
                            aria-hidden="true"
                          />
                          <Input
                            id="sales-scan-input"
                            innerRef={scanInputRef}
                            type="text"
                            autoComplete="off"
                            className="pos-field-input pos-scan-input"
                            placeholder={
                              selectedLocation
                                ? t("commerceCommon.scanInto").replace(
                                    "{location}",
                                    selectedLocation.name ||
                                      t("commerceCommon.selectedLocation")
                                  )
                                : t("commerceCommon.chooseLocationFirst")
                            }
                            value={scanValue}
                            onChange={(e) => setScanValue(e.target.value)}
                            disabled={!locationId || isScanning || isCreatingSale}
                            aria-label={t("storeSales.scanBarcode")}
                          />
                        </div>
                        <Button
                          color="primary"
                          type="submit"
                          className="pos-field-submit"
                          disabled={
                            !locationId || !scanValue.trim() || isScanning || isCreatingSale
                          }
                        >
                          {isScanning ? t("commerceCommon.scanning") : t("commerceCommon.scan")}
                        </Button>
                      </div>
                      <p className="pos-scan-hint">{t("storeSales.scanHint")}</p>
                    </Form>
                  )}
                </section>

                {basket.length > 0 ? (
                  <section className="pos-basket-card">
                    <div className="pos-basket-card__head">
                      <div className="pos-basket-card__heading">
                        <h2 className="pos-basket-title">{t("storeSales.cartTitle")}</h2>
                        <span className="pos-basket-count">
                          {itemCount === 1
                            ? t("commerceCommon.quantityItems").replace("{count}", itemCount)
                            : t("commerceCommon.quantityItemsPlural").replace(
                                "{count}",
                                itemCount
                              )}
                        </span>
                      </div>
                      <Button
                        color="secondary"
                        outline
                        className="pos-basket-clear"
                        onClick={() => setBasket([])}
                        disabled={isCreatingSale}
                      >
                        <i className="tim-icons icon-trash-simple" aria-hidden="true" />
                        {t("commerceCommon.clearBasket")}
                      </Button>
                    </div>

                    <div className="pos-basket-table sp-scrollbar-hidden">
                      <div className="pos-basket-row pos-basket-row--header" aria-hidden="true">
                        <span>#</span>
                        <span>{t("commerceCommon.itemHeader")}</span>
                        <span>{t("commerceCommon.price")}</span>
                        <span>{t("commerceCommon.qty")}</span>
                        <span>{t("commerceCommon.total")}</span>
                        <span>{t("commerceCommon.actions")}</span>
                      </div>
                      {basket.map((line, index) => {
                        const isEditingPrice = editingPriceVariantId === line.variantId;
                        const lineTitle =
                          line.productTitle ||
                          buildSaleItemDisplayLabel({
                            productTitle: line.productTitle,
                            variantTitle: line.variantTitle,
                            optionSummary: line.optionSummary,
                          });
                        const lineChips = buildOptionChips(line.optionSummary);
                        const isAggregateLine = line.inventoryMode === "aggregate";
                        const stockLimit = resolveLineStockLimit(line, locationId);
                        const atStockLimit = isLineAtStockLimit(line, locationId);
                        const stockResolved = hasResolvedLineAvailability(line, locationId);
                        const canIncrement = canIncrementLine(line, locationId);
                        const overStock =
                          stockLimit !== null && Number(line.quantity || 0) > stockLimit;
                        const lineImage = resolveItemImage(line);
                        return (
                          <div className="pos-basket-row" key={line.variantId}>
                            <span className="pos-basket-row__index">{index + 1}</span>
                            <div className="pos-basket-row__item has-media">
                              <span className="pos-basket-thumb">
                                {lineImage ? (
                                  <img src={lineImage} alt={line.imageAltText || ""} loading="lazy" />
                                ) : (
                                  <span className="sir-row__media-empty" aria-hidden="true" />
                                )}
                              </span>
                              <div className="pos-basket-row__info">
                                <span className="pos-item-label">{lineTitle}</span>
                                {line.sku && (
                                  <span className="pos-basket-row__meta sp-truncate">
                                    {line.sku}
                                  </span>
                                )}
                                {(lineChips.length > 0 || line.brandName) && (
                                  <div className="pos-result-row__chips">
                                    {line.brandName && (
                                      <span className="pos-result-chip">{line.brandName}</span>
                                    )}
                                    {lineChips.map((chip) => (
                                      <span className="pos-result-chip" key={chip}>
                                        {chip}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {line.discountTotalAmount > 0 && (
                                  <span className="pos-basket-row__discount sp-discount">
                                    {t("commerceCommon.discountLine").replace(
                                      "{amount}",
                                      formatMoney(line.discountTotalAmount)
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="pos-basket-row__price">
                              {isEditingPrice ? (
                                <div className="pos-price-edit">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={priceEditorValue}
                                    onChange={(e) => setPriceEditorValue(e.target.value)}
                                    disabled={isCreatingSale}
                                    aria-label={t("commerceCommon.price")}
                                  />
                                  <div className="pos-price-edit__actions">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleApplyPriceEdit(line)}
                                      disabled={isCreatingSale}
                                    >
                                      {t("commerceCommon.apply")}
                                    </Button>
                                    <Button
                                      color="secondary"
                                      size="sm"
                                      onClick={() => {
                                        setEditingPriceVariantId(null);
                                        setPriceEditorValue("");
                                      }}
                                      disabled={isCreatingSale}
                                    >
                                      {t("common.cancel")}
                                    </Button>
                                  </div>
                                </div>
                              ) : line.discountTotalAmount > 0 ? (
                                <>
                                  <span>{formatMoney(line.finalUnitPriceAmount)}</span>
                                  <span className="pos-basket-row__was">
                                    {t("commerceCommon.wasPrice").replace(
                                      "{amount}",
                                      formatMoney(line.baseUnitPriceAmount)
                                    )}
                                  </span>
                                </>
                              ) : (
                                <span>{formatMoney(line.finalUnitPriceAmount)}</span>
                              )}
                            </div>
                            <div className="pos-qty-cell">
                              <div className="pos-qty-stepper">
                                <button
                                  type="button"
                                  className="pos-qty-btn"
                                  onClick={() => handleRemoveOne(line.variantId)}
                                  disabled={isCreatingSale}
                                  aria-label={t("storeSales.decreaseQty")}
                                  title={t("storeSales.decreaseQty")}
                                >
                                  <span aria-hidden="true">−</span>
                                </button>
                                <span className="pos-qty-value">{line.quantity}</span>
                                <button
                                  type="button"
                                  className="pos-qty-btn"
                                  onClick={() => handleAddOne(line)}
                                  disabled={isCreatingSale || !canIncrement}
                                  aria-label={t("storeSales.increaseQty")}
                                  title={
                                    !isAggregateLine
                                      ? t("storeSales.serializedQtyHint")
                                      : !stockResolved
                                      ? t("storeSales.checkingStock")
                                      : atStockLimit
                                      ? t("storeSales.maxStockReached").replace(
                                          "{count}",
                                          stockLimit
                                        )
                                      : t("storeSales.increaseQty")
                                  }
                                >
                                  <span aria-hidden="true">+</span>
                                </button>
                              </div>
                              {isAggregateLine && !stockResolved && (
                                <span className="pos-qty-note">
                                  {isRevalidatingStock
                                    ? t("storeSales.checkingStock")
                                    : t("storeSales.stockUnresolvedShort")}
                                </span>
                              )}
                              {overStock ? (
                                <span className="pos-qty-limit">
                                  {t("storeSales.overStockShort").replace(
                                    "{count}",
                                    stockLimit
                                  )}
                                </span>
                              ) : (
                                atStockLimit && (
                                  <span className="pos-qty-limit">
                                    {t("storeSales.maxStockShort").replace(
                                      "{count}",
                                      stockLimit
                                    )}
                                  </span>
                                )
                              )}
                            </div>
                            <span className="pos-basket-row__total">
                              {formatMoney(line.lineSubtotalAmount)}
                            </span>
                            <div className="pos-basket-row__actions">
                              <Button
                                color="secondary"
                                size="sm"
                                className="pos-icon-btn"
                                onClick={() => handleStartPriceEdit(line)}
                                disabled={isCreatingSale}
                                aria-label={`${t("storeSales.editPrice")} — ${line.productTitle}`}
                                title={t("storeSales.editPrice")}
                              >
                                <i className="tim-icons icon-pencil" aria-hidden="true" />
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                className="pos-icon-btn pos-icon-btn--danger"
                                onClick={() => handleRemoveLine(line.variantId)}
                                disabled={isCreatingSale}
                                aria-label={`${t("storeSales.removeItem")} — ${line.productTitle}`}
                                title={t("storeSales.removeItem")}
                              >
                                <i className="tim-icons icon-trash-simple" aria-hidden="true" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ) : (
                  <div className="sp-empty sp-empty--compact">
                    <div className="sp-empty-icon">
                      <i className="tim-icons icon-cart" />
                    </div>
                    <div className="sp-empty-title">{t("storeSales.readyToSell")}</div>
                    <p className="sp-empty-desc">{t("storeSales.emptyBasketDesc")}</p>
                  </div>
                )}

                {basket.length > 0 && (
                  <div className="sales-page-checkout-mobile">{renderCheckoutPanel(false)}</div>
                )}
              </div>

              {basket.length > 0 && renderCheckoutPanel(true)}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default SalesPage;
